import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import CtaSection from '../components/sections/CtaSection';
import { OpenAIIcon, SalesforceIcon, WooCommerceIcon, WebhookIcon, ApiIcon, EmailIcon } from '../components/ui/IntegrationIcons';
import SEO from '../components/SEO';
import './IntegrationsPage.css';

const CATEGORIES = [
  {
    label: 'AI Models',
    items: [
      { name: 'OpenAI GPT-4',  Icon: OpenAIIcon,       color: '#10A37F', desc: 'Power your chatbot with the world\'s leading AI language model.' },
      { name: 'Google Gemini', slug: 'googlegemini',  color: '#4285F4', desc: 'Leverage Google\'s multimodal AI for smarter, richer responses.' },
      { name: 'Claude AI',     slug: 'anthropic',     color: '#D4723A', desc: 'Anthropic\'s safety-first AI for accurate, helpful conversations.' },
    ],
  },
  {
    label: 'E-Commerce',
    items: [
      { name: 'Shopify',       slug: 'shopify',       color: '#96BF48', desc: 'Auto-confirm orders and sync product catalogs via OAuth.' },
      { name: 'WooCommerce',   Icon: WooCommerceIcon,  color: '#7F54B3', desc: 'Webhook-based order confirmations and follow-ups for WordPress stores.' },
      { name: 'Stripe',        slug: 'stripe',        color: '#635BFF', desc: 'Payment status webhooks trigger WhatsApp receipts and updates.' },
    ],
  },
  {
    label: 'CRM & Sales',
    items: [
      { name: 'HubSpot',       slug: 'hubspot',       color: '#FF7A59', desc: 'Sync Zepliq contacts and deals directly into your HubSpot CRM.' },
      { name: 'Salesforce',    Icon: SalesforceIcon,   color: '#00A1E0', desc: 'Push qualified leads and conversation summaries to Salesforce.' },
      { name: 'Pipedrive',     slug: 'pipedrive',     color: '#00B187', desc: 'Create deals and move pipeline stages from chat signals.' },
    ],
  },
  {
    label: 'Automation',
    items: [
      { name: 'Zapier',           slug: 'zapier',       color: '#FF4A00', desc: 'Connect Zepliq to 5,000+ apps with no-code Zap workflows.' },
      { name: 'Make (Integromat)', slug: 'make',         color: '#6D0EDE', desc: 'Build advanced multi-step automations with visual flow builder.' },
      { name: 'Google Sheets',    slug: 'googlesheets', color: '#34A853', desc: 'Log every conversation event and lead update to a spreadsheet.' },
    ],
  },
  {
    label: 'Scheduling',
    items: [
      { name: 'Calendly',        slug: 'calendly',        color: '#006BFF', desc: 'Send booking links inside chat and capture appointments automatically.' },
      { name: 'Google Calendar', slug: 'googlecalendar',  color: '#4285F4', desc: 'Create calendar events directly from confirmed bookings.' },
      { name: 'Cal.com',         slug: 'caldotcom',       color: '#6366F1', desc: 'Open-source scheduling with full Zepliq webhook support.' },
    ],
  },
  {
    label: 'Developer',
    items: [
      { name: 'REST API',   slug: null, color: '#0EA5E9', Icon: ApiIcon,     desc: 'Full-featured API to integrate Zepliq into any custom application.' },
      { name: 'Webhooks',   slug: null, color: '#6366F1', Icon: WebhookIcon, desc: 'Push real-time events to any endpoint: leads, orders, replies.' },
      { name: 'SMTP Email', slug: null, color: '#EA4335', Icon: EmailIcon,   desc: 'Trigger email notifications alongside WhatsApp messages.' },
    ],
  },
];

const STATS = [
  { num: '50+',    label: 'Integrations available' },
  { num: '5,000+', label: 'Apps via Zapier' },
  { num: '< 5 min', label: 'Average setup time' },
  { num: '0',      label: 'Developers needed' },
];

export default function IntegrationsPage() {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, particles = [];
    const PARTICLE_COUNT = 55;
    const resize = () => { W = canvas.width = canvas.offsetWidth; H = canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);
    class Particle {
      constructor() { this.reset(); }
      reset() {
        this.x = Math.random() * W; this.y = Math.random() * H;
        this.r = Math.random() * 1.8 + 0.4;
        this.dx = (Math.random() - 0.5) * 0.25; this.dy = (Math.random() - 0.5) * 0.25;
        this.o = Math.random() * 0.5 + 0.15;
        this.hue = Math.random() > 0.6 ? 245 : 270;
      }
      update() { this.x += this.dx; this.y += this.dy; if (this.x < 0 || this.x > W || this.y < 0 || this.y > H) this.reset(); }
      draw() { ctx.beginPath(); ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2); ctx.fillStyle = `hsla(${this.hue}, 80%, 55%, ${this.o})`; ctx.fill(); }
    }
    for (let i = 0; i < PARTICLE_COUNT; i++) particles.push(new Particle());
    const drawLines = () => {
      for (let i = 0; i < particles.length; i++) for (let j = i + 1; j < particles.length; j++) {
        const dx = particles[i].x - particles[j].x, dy = particles[i].y - particles[j].y;
        const d = Math.sqrt(dx*dx + dy*dy);
        if (d < 100) { ctx.beginPath(); ctx.moveTo(particles[i].x, particles[i].y); ctx.lineTo(particles[j].x, particles[j].y); ctx.strokeStyle = `rgba(99,102,241,${0.06*(1-d/100)})`; ctx.lineWidth = 0.5; ctx.stroke(); }
      }
    };
    const animate = () => { ctx.clearRect(0,0,W,H); particles.forEach(p=>{p.update();p.draw();}); drawLines(); animRef.current = requestAnimationFrame(animate); };
    animate();
    return () => { window.removeEventListener('resize', resize); cancelAnimationFrame(animRef.current); };
  }, []);

  return (
    <div className="integ-page">
      <SEO
        title="Integrations — Connect WhatsApp to Shopify, WooCommerce & More"
        description="Zepliq integrates with Shopify, WooCommerce, OpenAI, Salesforce, Webhooks, and more. Automate WhatsApp order confirmations, sync customer data, and connect your entire sales stack in minutes."
        keywords="Shopify WhatsApp integration, WooCommerce WhatsApp bot, WhatsApp Shopify automation, WhatsApp CRM integration, OpenAI WhatsApp, WhatsApp webhook, ecommerce WhatsApp automation, connect WhatsApp to Shopify"
        canonical="/integrations"
      />

      {/* ── HERO ── */}
      <section className="integ-hero">
        <canvas ref={canvasRef} className="integ-hero__canvas" aria-hidden="true" />
        <div className="integ-hero__blob integ-hero__blob--1" aria-hidden="true" />
        <div className="integ-hero__blob integ-hero__blob--2" aria-hidden="true" />
        <div className="container integ-hero__inner">
          <span className="badge badge-brand">Integrations</span>
          <h1 className="display-xl integ-hero__title">
            Plug Zepliq into<br />
            <span className="gradient-text">your entire stack.</span>
          </h1>
          <p className="text-lg integ-hero__sub">
            Connect the AI models, e-commerce platforms, CRMs, and automation tools you already use, in minutes, with no developer required.
          </p>
          <div className="integ-hero__actions">
            <Link to="/contact" className="btn btn-primary btn-lg">
              Start Free
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </Link>
            <Link to="/features" className="btn btn-secondary btn-lg">See All Features</Link>
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="integ-stats section-sm">
        <div className="container">
          <div className="integ-stats__grid">
            {STATS.map((s, i) => (
              <div key={i} className="integ-stats__item">
                <span className="integ-stats__num gradient-text">{s.num}</span>
                <span className="integ-stats__label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CATEGORY SECTIONS ── */}
      {CATEGORIES.map((cat, ci) => (
        <section
          key={cat.label}
          className={`integ-cat section-sm ${ci % 2 === 0 ? 'integ-cat--surface' : 'integ-cat--base'}`}
        >
          <div className="container">
            <h2 className="integ-cat__label">{cat.label}</h2>
            <div className="integ-cat__grid">
              {cat.items.map((item, ii) => (
                <div key={ii} className="integ-card" style={{ '--ic': item.color }}>
                  <div className="integ-card__logo-wrap">
                    {item.Icon
                      ? <item.Icon size={24} />
                      : <img
                          src={`https://cdn.simpleicons.org/${item.slug}/${item.color.slice(1)}`}
                          alt={item.name}
                          width="24"
                          height="24"
                          loading="lazy"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                    }
                  </div>
                  <div>
                    <h3 className="integ-card__name">{item.name}</h3>
                    <p className="integ-card__desc">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* ── API NOTE ── */}
      <section className="integ-api section">
        <div className="container integ-api__inner">
          <div className="integ-api__copy">
            <span className="badge badge-accent">REST API & Webhooks</span>
            <h2 className="display-lg integ-api__title">
              Build any integration<br />
              <span className="gradient-text">with our open API.</span>
            </h2>
            <p className="text-lg integ-api__desc">
              Every Zepliq feature is accessible via our REST API. Send messages, manage contacts, read conversations, trigger automations, from any language, any platform.
            </p>
            <div className="integ-api__actions">
              <Link to="/contact" className="btn btn-primary">
                Get API access
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </Link>
            </div>
          </div>
          <div className="integ-api__code">
            <div className="integ-api__code-header">
              <span className="integ-api__code-dot" style={{ background: '#FF5F57' }} />
              <span className="integ-api__code-dot" style={{ background: '#FEBC2E' }} />
              <span className="integ-api__code-dot" style={{ background: '#28C840' }} />
              <span className="integ-api__code-lang">POST /v1/messages</span>
            </div>
            <pre className="integ-api__pre"><code>{`{
  "to": "+1234567890",
  "channel": "whatsapp",
  "message": "Hi {{name}}, your
    order #{{order_id}} is confirmed!",
  "variables": {
    "name": "Sarah",
    "order_id": "WC-4821"
  }
}`}</code></pre>
          </div>
        </div>
      </section>

      <CtaSection />
    </div>
  );
}
